import React, { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams, useHistory } from 'react-router-dom'
import { ArrowLeft, Trash2 } from 'react-feather'
import Select from 'react-select'
import { Editor } from '@bytemd/react'
import gfm from '@bytemd/plugin-gfm'
import breaks from '@bytemd/plugin-breaks'
import highlight from '@bytemd/plugin-highlight'
import useAxios from 'axios-hooks'
import 'bytemd/dist/index.css'

import BreadcrumbHeader from '~/components/BreadcrumbHeader'
import { ConfirmDeletion, CreateWrapper } from './styles'
import { MODULES_DATA } from '~/constants/tutorialModules'
import pt from 'assets/data/markdown/pt.json'
import { toast } from 'react-toastify'
import { Modal } from 'reactstrap'
import DropzoneImageInput from '~/components/DropzoneImageInput'
import uploadFile from '~/api/uploadFile'

const TWO_MB_IN_BYTES = 2 * 1024 * 1024

const CreateTutorial = () => {
  const intl = useIntl()
  const history = useHistory()
  const { tutorialId } = useParams()
  const [title, setTitle] = React.useState('')
  const [videoLink, setVideoLink] = React.useState('')
  const [thumbnail, setThumbnail] = React.useState(null)
  const [content, setContent] = React.useState('')
  const [module, setModule] = React.useState(null)
  const [submodule, setSubmodule] = React.useState(null)
  const [isEdit, setIsEdit] = React.useState(false)
  const [deleteConfirmModal, setDeleteConfirmModal] = React.useState(false)

  const [{ loading, error, data }, getTutorial] = useAxios(
    {
      url: `/tutorial/${tutorialId}`,
      method: 'GET',
    },
    {
      manual: true,
    }
  )

  const [, createTutorial] = useAxios(
    {
      url: `/tutorial`,
      method: 'POST',
    },
    {
      manual: true,
    }
  )

  const [, updateTutorial] = useAxios(
    {
      url: `/tutorial/${tutorialId}`,
      method: 'PUT',
    },
    {
      manual: true,
    }
  )

  const [, deleteTutorial] = useAxios(
    {
      url: `/tutorial/${tutorialId}`,
      method: 'DELETE',
    },
    {
      manual: true,
    }
  )

  async function handleSubmit() {
    if (!title || !module || !submodule) {
      toast.error(intl.formatMessage({ id: 'Please fill all required fields' }))
      return
    }

    if (videoLink) {
      try {
        const url = new URL(videoLink)
        if (url.host !== 'vimeo.com') throw new Error()
      } catch (error) {
        toast.error(
          intl.formatMessage({ id: 'Video link must be a valid vimeo URL' })
        )
        return
      }
    }

    let image = null
    if (thumbnail?.length) {
      try {
        const { data } = await uploadFile(thumbnail)
        image = data.uri
      } catch (error) {
        toast.error(intl.formatMessage({ id: 'Failed to upload image' }))
        return
      }
    }

    const data = {
      title,
      video: videoLink,
      image,
      content,
      module: module.value,
      submodule: submodule.value,
    }

    if (isEdit) {
      updateTutorial({ data })
        .then(() => {
          toast.success(intl.formatMessage({ id: 'Tutorial updated' }))
          getTutorial()
        })
        .catch(() => {
          toast.error(intl.formatMessage({ id: 'Failed to update tutorial' }))
        })
    } else {
      createTutorial({ data })
        .then(res => {
          toast.success(
            intl.formatMessage({ id: 'Tutorial created successfully' })
          )
          setTitle('')
          setVideoLink('')
          setModule(null)
          setSubmodule(null)
          setContent('')
          setThumbnail(null)
          history.push(`/tutorial/${res.data.id}`)
        })
        .catch(() => {
          toast.error(intl.formatMessage({ id: 'Failed to create tutorial' }))
        })
    }
  }

  function handleDelete() {
    deleteTutorial()
      .then(() => {
        toast.success(intl.formatMessage({ id: 'Tutorial deleted' }))
        history.push('/tutorials')
      })
      .catch(() => {
        toast.error(intl.formatMessage({ id: 'Failed to delete tutorial' }))
      })
  }

  async function handleUploads(files) {
    const images = await Promise.all(
      files.map(async file => {
        return await uploadFile(file)
      })
    )
    const uploadedImages = images.map(({ data }) => ({ url: data.uri }))

    return uploadedImages
  }

  useEffect(() => {
    if (tutorialId) getTutorial()
    setIsEdit(Boolean(tutorialId))
  }, [tutorialId])

  useEffect(() => {
    if (!error) return
    toast.error(intl.formatMessage({ id: 'Failed to fetch tutorial' }))
    history.push('/tutorials')
  }, [error])

  useEffect(() => {
    if (!data) return
    setTitle(data.title)
    setVideoLink(data.video)
    setModule({
      value: data.module,
      label: intl.formatMessage({ id: MODULES_DATA[data.module].name }),
    })
    setSubmodule({
      value: data.submodule,
      label: intl.formatMessage({
        id: MODULES_DATA[data.module].submodules[data.submodule],
      }),
    })
    setContent(data.content)
    if (data.image) {
      const extension = data.image.split('.').pop()
      fetch(data.image)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `image.${extension}`, {
            type: `image/${extension}`,
          })
          const fileUrl = URL.createObjectURL(file)
          file.preview = fileUrl
          file.id = `${new Date().getTime()}`
          file.path = file.name
          setThumbnail([file])
        })
    }
  }, [data])

  return (
    <div>
      <BreadcrumbHeader
        path={[
          { name: 'Tutorials', path: '/tutorials' },
          isEdit ? { name: 'Edit tutorial' } : { name: 'New tutorial' },
        ]}
      />

      <Modal
        isOpen={deleteConfirmModal}
        toggle={setDeleteConfirmModal}
        centered={true}
        size="md"
      >
        <ConfirmDeletion>
          <div>
            <Trash2 color="#ED5F5F" width="5.625rem" height="5.625rem" />
          </div>
          <div className="confirm-text">
            <FormattedMessage id="Would you like to remove this tutorial?" />
          </div>
          <div className="info-container">
            <div className="info-text">
              <FormattedMessage id="By confirming this action the item below will be deleted:" />
            </div>
            <div className="title-text">{title}</div>
          </div>
          <div className="action-container">
            <button className="delete-button" onClick={handleDelete}>
              <FormattedMessage id="Yes, delete it!" />
            </button>

            <button
              className="cancel-button"
              onClick={() => setDeleteConfirmModal(false)}
            >
              <FormattedMessage id="Cancel" />
            </button>
          </div>
        </ConfirmDeletion>
      </Modal>

      <CreateWrapper>
        <div className="context-menu">
          <a href="/tutorials">
            <ArrowLeft color="#2D3748" width="1rem" height="1rem" />
          </a>
          <h2>
            {isEdit ? (
              <FormattedMessage id="Edit tutorial" />
            ) : (
              <FormattedMessage id="New tutorial" />
            )}
          </h2>
        </div>
        <div className="tutorial-inputs">
          <div className="simple-input">
            <label htmlFor="tutorial-title">
              <FormattedMessage id="Title" />*
            </label>
            <input
              id="tutorial-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="simple-input">
            <label htmlFor="tutorial-video">
              <FormattedMessage id="Video Link" />
            </label>
            <input
              id="tutorial-video"
              value={videoLink}
              onChange={e => setVideoLink(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="select-input">
            <label>
              <FormattedMessage id="Content thumbnail" />
            </label>
            <DropzoneImageInput
              value={thumbnail || []}
              onChange={f => setThumbnail(f)}
              maxFileSize={TWO_MB_IN_BYTES}
              altBehavior
            />
          </div>
          <div className="double-select">
            <div className="select-input">
              <label htmlFor="tutorial-module">
                <FormattedMessage id="Module" />*
              </label>
              <Select
                value={module}
                onChange={v => {
                  if (!MODULES_DATA[v?.value]?.submodules?.[submodule?.value])
                    setSubmodule(null)
                  setModule(v)
                }}
                options={Object.entries(MODULES_DATA).map(([key, value]) => ({
                  value: key,
                  label: intl.formatMessage({ id: value.name }),
                }))}
                placeholder={intl.formatMessage({ id: 'Select' })}
                isDisabled={loading}
                isClearable
              />
            </div>
            <div className="select-input">
              <label htmlFor="tutorial-submodule">
                <FormattedMessage id="Submodule" />*
              </label>
              <Select
                value={submodule}
                onChange={v => setSubmodule(v)}
                options={Object.entries(
                  MODULES_DATA[module?.value]?.submodules || {}
                ).map(([key, value]) => ({
                  value: key,
                  label: intl.formatMessage({ id: value }),
                }))}
                placeholder={intl.formatMessage({ id: 'Select' })}
                isDisabled={!module || loading}
                isClearable
              />
            </div>
          </div>
        </div>
        <div className="markdown-input">
          <Editor
            value={content}
            onChange={v => setContent(v)}
            plugins={[gfm(), breaks(), highlight()]}
            uploadImages={handleUploads}
            mode="split"
            locale={intl.locale === 'br' ? pt : undefined}
            disabled={loading}
          />
        </div>
        <div className="action-buttons">
          <div>
            {isEdit && (
              <button
                className="delete-button"
                disabled={loading}
                onClick={() => setDeleteConfirmModal(true)}
              >
                <Trash2 color="#ED5F5F" width="1.25rem" height="1.25rem" />
                <div>
                  <FormattedMessage id="Delete content" />
                </div>
              </button>
            )}
          </div>
          <div>
            <button
              className="cancel-button"
              onClick={() => history.push('/tutorials')}
              disabled={loading}
            >
              <FormattedMessage id="Cancel" />
            </button>
            <button
              className="save-button"
              disabled={loading}
              onClick={handleSubmit}
            >
              <FormattedMessage id="Save changes" />
            </button>
          </div>
        </div>
      </CreateWrapper>
    </div>
  )
}

export default CreateTutorial
